import { apiClient } from './client'
import { filenameFromHeaders } from './csvDownload'
import type { ApiResponse } from '@/types/api'

// ============================================================================
// Shared types
// ============================================================================

export type InquiryStatus = 'pending' | 'contacted' | 'done'

// 사업설명회 신청은 기존 3상태 + 관리자 수동 라벨 '중복접수'를 추가로 가진다.
// (franchise 등 다른 문의는 InquiryStatus 그대로 사용)
export type BriefingStatus = InquiryStatus | 'duplicate'

interface PageEnvelope<T> {
  content: T[]
  page: number
  size: number
  totalElements: number
  totalPages: number
  first: boolean
  last: boolean
}

// ============================================================================
// Briefing
// ============================================================================

export type SessionMode = 'offline' | 'online'
export type BriefingAttendanceStatus = 'ATTENDED' | 'NO_SHOW' | 'UNKNOWN'

export interface SessionAvailability {
  sessionDate: string
  sessionMode: SessionMode
  full: boolean
}

export interface BriefingApplicationListItem {
  id: number
  name: string
  phone: string
  sessionDate: string
  sessionMode: SessionMode
  sessionId: string | null
  participationStatus: 'active' | 'cancelled'
  /** 설명회 실제 참석 여부 — participationStatus(신청 취소)와 별개 */
  attendanceStatus: 'ATTENDED' | 'NO_SHOW' | 'UNKNOWN'
  franchiseTypes: string[]
  sido: string | null
  sigungu: string | null
  interests: string[]
  otherText: string | null
  privacyConsentedAt: string | null
  marketingConsent: boolean
  marketingConsentedAt: string | null
  status: BriefingStatus
  createdAt: string
  updatedAt: string
}

// 상세 수정(전체 입력 필드). 개인정보 동의·신청일시 등 시스템/법적 기록은 제외.
export interface BriefingApplicationUpdatePayload {
  name: string
  phone: string
  franchiseTypes: string[]
  sido: string | null
  sigungu: string | null
  interests: string[]
  otherText: string | null
  marketingConsent: boolean
}

export const briefingApplicationApi = {
  // 한 릴리스 동안 유지되는 legacy 날짜/방식 projection 어댑터.
  availability: async (): Promise<SessionAvailability[]> => {
    const res = await apiClient.get<ApiResponse<SessionAvailability[]>>(
      '/v1/public/lc-marketing/briefing-applications/availability',
    )
    if (!res.data.data) {
      throw new Error('Failed to fetch briefing availability')
    }
    return res.data.data
  },
  list: async (
    params: {
      status?: BriefingStatus
      sessionDate?: string
      sessionId?: string
      search?: string
      sessionMode?: SessionMode
      page?: number
      size?: number
    } = {},
  ): Promise<PageEnvelope<BriefingApplicationListItem>> => {
    const res = await apiClient.get<ApiResponse<PageEnvelope<BriefingApplicationListItem>>>(
      '/admin/lc-marketing/briefing-applications',
      { params },
    )
    if (!res.data.data) {
      throw new Error('Failed to fetch briefing applications')
    }
    return res.data.data
  },
  updateStatus: async (id: number, status: BriefingStatus): Promise<void> => {
    await apiClient.patch<ApiResponse<{ status: string }>>(
      `/admin/lc-marketing/briefing-applications/${id}/status`,
      { status },
    )
  },
  update: async (
    id: number,
    payload: BriefingApplicationUpdatePayload,
  ): Promise<BriefingApplicationListItem> => {
    const res = await apiClient.patch<ApiResponse<BriefingApplicationListItem>>(
      `/admin/lc-marketing/briefing-applications/${id}`,
      payload,
    )
    if (!res.data.data) {
      throw new Error('Failed to update briefing application')
    }
    return res.data.data
  },
  move: async (id: number, targetSessionId: string): Promise<void> => {
    await apiClient.post(`/admin/lc-marketing/briefing-applications/${id}/move`, {
      targetSessionId,
    })
  },
  cancelParticipation: async (id: number): Promise<void> => {
    await apiClient.post(`/admin/lc-marketing/briefing-applications/${id}/cancel`)
  },
  /** 설명회 실제 참석 여부 기록. 원장에 기록되고 CRM 접수함 사본에 자동 반영된다. */
  updateAttendance: async (
    id: number,
    attendanceStatus: 'ATTENDED' | 'NO_SHOW' | 'UNKNOWN',
  ): Promise<void> => {
    await apiClient.patch<ApiResponse<{ attendanceStatus: string }>>(
      `/admin/lc-marketing/briefing-applications/${id}/attendance`,
      { attendanceStatus },
    )
  },
  export: async (
    params: { status?: BriefingStatus; sessionDate?: string; sessionId?: string; search?: string; sessionMode?: SessionMode } = {},
  ): Promise<{ blob: Blob; filename: string }> => {
    const res = await apiClient.get<Blob>(
      '/admin/lc-marketing/briefing-applications/export',
      { params, responseType: 'blob' },
    )
    return {
      blob: res.data,
      filename: filenameFromHeaders(res.headers, 'briefing-applications'),
    }
  },
}

export type BriefingSessionEffectiveStatus = 'published' | 'completed' | 'cancelled'
export type BriefingSessionAvailability = 'open' | 'full' | 'closed'

export interface BriefingSessionAdmin {
  id: number
  sessionId: string
  startsAt: string
  endsAt: string
  applicationClosesAt: string
  mode: SessionMode
  capacity: number | null
  status: 'published' | 'cancelled'
  effectiveStatus: BriefingSessionEffectiveStatus
  manuallyClosed: boolean
  applicationAvailability: BriefingSessionAvailability
  activeApplicationCount: number
  attendedCount: number
  noShowCount: number
  unknownAttendanceCount: number
  version: number
  inPublicWindow: boolean
  latestChangeId: number | null
}

export interface BriefingSessionInput {
  startsAt: string
  endsAt: string
  applicationClosesAt: string
  mode: SessionMode
  capacity: number | null
}

export interface BriefingSessionPreview {
  checksum: string
  sessions: Array<{
    session: BriefingSessionInput
    sessionDateKst: string
    dateConflict: boolean
    inPublicWindowAfterCreate: boolean
  }>
}

export interface BriefingSessionContact {
  id: number
  applicationId: number
  applicantName: string
  phone: string
  outcome: 'pending' | 'attending' | 'moved' | 'cancelled' | 'unreached'
  targetSessionId: string | null
  contactedAt: string | null
  note: string | null
  version: number
}

export interface BriefingAttendanceRoster {
  sessionId: string
  applications: BriefingApplicationListItem[]
  summary: { total: number; attended: number; noShow: number; unknown: number; cancelled: number }
}

export const briefingSessionApi = {
  attendance: async (id: number, search?: string): Promise<BriefingAttendanceRoster> => {
    const response = await apiClient.get<ApiResponse<BriefingAttendanceRoster>>(
      `/admin/lc-marketing/briefing-sessions/${id}/attendance`,
      { params: { search: search || undefined } },
    )
    if (!response.data.data) throw new Error('Failed to fetch attendance roster')
    return response.data.data
  },
  saveAttendance: async (
    id: number,
    entries: Array<{ applicationId: number; attendanceStatus: BriefingAttendanceStatus }>,
  ): Promise<BriefingAttendanceRoster> => {
    const response = await apiClient.patch<ApiResponse<BriefingAttendanceRoster>>(
      `/admin/lc-marketing/briefing-sessions/${id}/attendance`,
      { entries },
    )
    if (!response.data.data) throw new Error('Failed to save attendance roster')
    return response.data.data
  },
  list: async (params: {
    status?: string
    mode?: SessionMode
    from?: string
    to?: string
    availability?: BriefingSessionAvailability
  } = {}): Promise<BriefingSessionAdmin[]> => {
    const response = await apiClient.get<ApiResponse<BriefingSessionAdmin[]>>(
      '/admin/lc-marketing/briefing-sessions',
      { params },
    )
    if (!response.data.data) throw new Error('Failed to fetch briefing sessions')
    return response.data.data
  },
  preview: async (sessions: BriefingSessionInput[]): Promise<BriefingSessionPreview> => {
    const response = await apiClient.post<ApiResponse<BriefingSessionPreview>>(
      '/admin/lc-marketing/briefing-sessions/preview',
      { sessions },
    )
    if (!response.data.data) throw new Error('Failed to preview briefing sessions')
    return response.data.data
  },
  create: async (sessions: BriefingSessionInput[], previewChecksum: string) => {
    const response = await apiClient.post<ApiResponse<BriefingSessionAdmin[]>>(
      '/admin/lc-marketing/briefing-sessions',
      { sessions, previewChecksum },
    )
    return response.data.data ?? []
  },
  update: async (
    id: number,
    payload: BriefingSessionInput & { expectedVersion: number; reason?: string },
  ): Promise<BriefingSessionAdmin> => {
    const response = await apiClient.patch<ApiResponse<BriefingSessionAdmin>>(
      `/admin/lc-marketing/briefing-sessions/${id}`,
      payload,
    )
    if (!response.data.data) throw new Error('Failed to update briefing session')
    return response.data.data
  },
  changePreview: async (
    id: number,
    payload: BriefingSessionInput & { expectedVersion: number; reason?: string },
  ): Promise<{
    activeApplicationCount: number
    contactTargetCount: number
    contactTargets: Array<{
      applicationId: number
      applicantName: string
      phone: string
    }>
    capacityConflict: boolean
    dateConflict: boolean
    inPublicWindowAfterChange: boolean
  }> => {
    const response = await apiClient.post<ApiResponse<{
      activeApplicationCount: number
      contactTargetCount: number
      contactTargets: Array<{
        applicationId: number
        applicantName: string
        phone: string
      }>
      capacityConflict: boolean
      dateConflict: boolean
      inPublicWindowAfterChange: boolean
    }>>(`/admin/lc-marketing/briefing-sessions/${id}/change-preview`, payload)
    if (!response.data.data) throw new Error('Failed to preview briefing session change')
    return response.data.data
  },
  cancel: async (id: number, expectedVersion: number, reason?: string) => {
    await apiClient.post(`/admin/lc-marketing/briefing-sessions/${id}/cancel`, {
      expectedVersion,
      reason,
    })
  },
  setClosed: async (id: number, expectedVersion: number, closed: boolean) => {
    await apiClient.post(
      `/admin/lc-marketing/briefing-sessions/${id}/${closed ? 'close-applications' : 'reopen-applications'}`,
      { expectedVersion },
    )
  },
  contacts: async (changeId: number): Promise<BriefingSessionContact[]> => {
    const response = await apiClient.get<ApiResponse<BriefingSessionContact[]>>(
      `/admin/lc-marketing/briefing-session-changes/${changeId}/contacts`,
    )
    return response.data.data ?? []
  },
  updateContact: async (
    contactId: number,
    payload: {
      expectedVersion: number
      outcome: BriefingSessionContact['outcome']
      targetSessionId?: string
      note?: string
    },
  ) => {
    await apiClient.patch(
      `/admin/lc-marketing/briefing-session-change-contacts/${contactId}`,
      payload,
    )
  },
}

// ============================================================================
// Franchise
// ============================================================================

export type FranchiseType = 'academy' | 'tutoring'

export interface FranchiseInquiryListItem {
  id: number
  name: string
  phone: string
  franchiseTypes: FranchiseType[]
  sido: string | null
  sigungu: string | null
  inquiryText: string
  privacyConsentedAt: string | null
  marketingConsent: boolean
  marketingConsentedAt: string | null
  status: InquiryStatus
  createdAt: string
  updatedAt: string
}

export const franchiseInquiryApi = {
  list: async (
    params: {
      status?: InquiryStatus
      franchiseType?: FranchiseType
      sido?: string
      page?: number
      size?: number
    } = {},
  ): Promise<PageEnvelope<FranchiseInquiryListItem>> => {
    const res = await apiClient.get<ApiResponse<PageEnvelope<FranchiseInquiryListItem>>>(
      '/admin/lc-marketing/franchise-inquiries',
      { params },
    )
    if (!res.data.data) {
      throw new Error('Failed to fetch franchise inquiries')
    }
    return res.data.data
  },
  updateStatus: async (id: number, status: InquiryStatus): Promise<void> => {
    await apiClient.patch<ApiResponse<{ status: string }>>(
      `/admin/lc-marketing/franchise-inquiries/${id}/status`,
      { status },
    )
  },
  export: async (
    params: { status?: InquiryStatus; franchiseType?: FranchiseType; sido?: string } = {},
  ): Promise<{ blob: Blob; filename: string }> => {
    const res = await apiClient.get<Blob>(
      '/admin/lc-marketing/franchise-inquiries/export',
      { params, responseType: 'blob' },
    )
    return {
      blob: res.data,
      filename: filenameFromHeaders(res.headers, 'franchise-inquiries'),
    }
  },
}
